import React, { useState, useMemo } from 'react'
import { object, string, PropTypes, array } from 'prop-types'
import _capitalize from 'lodash/capitalize'
import { decodeTrademarkEntity, titleCase } from '@helpers/string-helper'
import { removeEmptyPropertiesFromObject } from '@helpers/array-helper'
import { analyticsProduct } from '@helpers/google-tag-manager'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import {
  productPrice,
  isProductStrikeThrough,
  getProductSaleFlagType,
  SaleFlagType,
  earliestStockDate,
  getImagesForProductTile,
  productExtraAndVariantTag,
  sectionalExtra,
  isDropShipping,
  isFreeShipping,
  productAvailability,
  getStockMessageSync,
  productOnSale,
  determineMoreRoomOptionsAvailable,
} from '@helpers/product'
import { useIsFavorited, useUpdateFavorites } from '@hooks/auth'
import { getRegionZone } from '@helpers/geo-location'
import { productUrl } from '@helpers/route'
import { navigate } from '@helpers/link'
import { useCart } from '@components/cart/hooks'
import { GROWTHBOOK_VARIANTS } from '@constants/growthbook-flags'
import { PRODUCT_TILE, PRODUCT_TILE_COMPARISON, PRODUCT_TILE_COUPON } from '../@rtg2022'
import useProductQuery from '../lib/hooks/useProductQuery'
import { useProduct } from './product/hooks'

const ProductTile = ({
  product: data,
  sku,
  handleAnalytics,
  index,
  source,
  selectedPill,
  sale,
  currentSku,
  comparisonFields,
  disableTrackingData,
  topLevelIsLoading,
  orderedProductVariations,
  shopCollection = false,
  featureVariant = 'default',
  isYMAL = false,
}) => {
  // Set active "variant" to the SKU in "useState"
  const [variant, setVariant] = useState(data?.sku || sku)
  // Then fetch the product data matching active "variant"
  const [enabled, setEnabled] = useState({ firstLoad: !data, prefetchVariations: !data })
  const isShowFinanceMessageFeatureOn = useFeatureIsOn(GROWTHBOOK_VARIANTS[featureVariant]?.showFinance)
  const { product: queryProduct, isLoading, isError, inViewRef, variations: fetchedVariations } = useProductQuery(
    variant,
    true,
    true,
    enabled,
    data,
  )

  const variations = useMemo(() => orderedProductVariations ?? fetchedVariations, [
    fetchedVariations,
    orderedProductVariations,
  ])
  const productVariations = useMemo(() => variations?.team || variations?.color || variations?.finish || [], [
    variations,
  ])

  const product = useMemo(() => {
    if (featureVariant === 'plp') {
      const productData = removeEmptyPropertiesFromObject(productVariations?.find(p => p.sku === variant))
      return { ...data, ...productData, ...queryProduct }
    }
    return queryProduct
  }, [data, featureVariant, queryProduct, variant, productVariations])

  const { financeAmount, financeNumMonths, promotionalPrice, promotionalMonths, promoMessageHTML } = useProduct({
    product,
  })

  const brandDisplayName = {
    'sound tech': 'Sound Tech by Shaq',
  }

  const ProductTileComponent = source === 'similar-products-v2' ? PRODUCT_TILE_COMPARISON : PRODUCT_TILE

  // Update "variant" to force ProductTile update when a variant swatch has been clicked

  const updateVariant = id => {
    setVariant(id)
    if (id !== variant && !enabled.prefetchVariations) {
      setEnabled(p => ({ ...p, prefetchVariations: true }))
    }
  }
  // Is the current sku included in the user's favorites?
  const isChecked = useIsFavorited(variant)
  // Get current region to control individual variant availability
  const { region, distribution_index, zone } = getRegionZone()
  //  Get the favorites and setter from the store
  const handleUpdateFavorites = useUpdateFavorites(product, index)
  // Availability date
  const earliestStockDateValue = earliestStockDate(product, distribution_index)
  // Get sale flag text
  const allowInStockSaleFlag = ['similar-products-v2'].includes(source)
  const saleFlagTypeLabel = getProductSaleFlagType(
    { product, availabilityDate: earliestStockDateValue },
    allowInStockSaleFlag,
  )
  const productIsOnSale = saleFlagTypeLabel !== SaleFlagType.InStock && saleFlagTypeLabel !== SaleFlagType.None // make sure to include closeout items, sale and others that also shows as sale
  const showSalePriceOnFavorites = productOnSale(product)

  // Product images (1rst is primary shown image, 2nd is 1rst alternate image)
  const productImages = product ? getImagesForProductTile(product) : []

  // If the product has dropshipping and free shipping, incluide "free shipping" on separate red line insted of regular inline
  const extraExcludesFreeShipping = !(isFreeShipping(product) && isDropShipping(product))
  const productExtraAndVariantTagLabel = productExtraAndVariantTag(
    product,
    variations,
    extraExcludesFreeShipping,
    region,
  )

  const sectionalExtraLabel = sectionalExtra(product)
  const isProductTileYMAL = isYMAL || source === 'add-to-cart-modal'
  const stockMessage = useMemo(() => getStockMessageSync(product)?.stockMessage, [product])
  const { renderAddToCartModal, onAddToCart } = useCart({
    quantity: 1,
    product,
    componentPage: 'favorites',
    stockMessage,
    warrantyEnabled: false,
    showLocation: false,
    moreInfoButton: false,
    region,
    isYMALItem: source === 'add-to-cart-modal',
    index,
    source,
  })

  const pricing = useMemo(() => {
    if (!data && !product) return {}
    const productData = product?.sku ? product : data
    const isCloseout = isProductStrikeThrough(productData)

    return {
      price: productPrice(productData),
      default_price: product?.pricing?.default_price || data?.default_price,
      strikeThrough: isCloseout,
      strikeThroughPrice: productPrice(productData, isCloseout),
    }
  }, [data, product])

  const availableColors = useMemo(() => {
    // FD-2403 Remove swatch for mattresses and adjustable base
    // if some more categories gets addes, this should be on a separate const
    if (
      !product ||
      ['mattress bedding', 'adjustable base only bedding', 'adjustable bedding', 'mattress foundations'].includes(
        product.category,
      )
    )
      return []
    let colors = variations?.team || variations?.color || variations?.finish
    if (!colors && product.swatch_image) {
      colors = [
        {
          sku: product.sku,
          image: product.swatch_image,
          variation_value: _capitalize(product?.color?.[0] || product?.finish?.[0]),
        },
      ]
    }

    if (
      (!data || ['similar-products-v2', 'strapi-slider', 'complete-your-room', 'may-also-like'].includes(source)) &&
      colors &&
      colors.length > 1
    ) {
      return colors.filter(item => item?.catalog_availability?.[region])
    }
    return colors || []
  }, [product, variations, data, source, region])

  const trackingData = useMemo(() => {
    if (!product || disableTrackingData) return {}
    let sliderTitle = ''
    if (product && product.sliderTitle) sliderTitle = `${product.sliderTitle}`
    else if (source) sliderTitle = `${source.replace(/[-_]/g, ' ')}`

    return {
      category: 'product tile',
      action: 'click',
      event: 'ee_click',
      label: product.sku,
      value: index + 1,
      ecommerce: {
        click: {
          position: index,
          products: [analyticsProduct(product, 1, index)],
          list: sliderTitle ? `${sliderTitle}` : `${source && source.replace(/[-_]/g, ' ')}`,
        },
      },
      ...(selectedPill ? { selectedPill } : {}),
    }
  }, [product, source, index, selectedPill, disableTrackingData])

  const rating = useMemo(
    () => ({
      reviews: product?.reviews,
      averageRating: product?.averageRating,
      ratings: product?.ratings,
    }),
    [product],
  )

  // Abstract the required data to properly render ProductTile
  const props = {
    ...pricing,
    primary_image: productImages[0],
    alternate_image: productImages[1],
    type: product?.type,
    deliveryType: product?.delivery_type,
    productCategory: product?.category,
    subCategories: product?.sub_category,
    brand: brandDisplayName[product?.brand] || titleCase(product?.brand || '', [' ', ',', '-', "'"]),
    feature: titleCase(product?.feature || '', [' ', ',', '-']),
    title: decodeTrademarkEntity(product?.displayNamePDP || product?.title || '', [' ', ',', '-']),
    sku: product?.sku,
    route: productUrl(product?.route),
    sale: source === 'favorites' ? showSalePriceOnFavorites : sale,
    displaySaleFlag: productIsOnSale,
    financeAmount,
    financeNumMonths,
    promotionalPrice,
    promotionalMonths,
    promoMessageHTML,
    availableColorVariations: availableColors,
    finish: variations?.finish,
    size: variations?.size,
    saleFlagLabel: saleFlagTypeLabel,
    productExtraLabel: productExtraAndVariantTagLabel,
    isDropShipping: isDropShipping(product),
    isFreeShipping: isFreeShipping(product),
    isAvailableForRegion: data ? true : productAvailability(product),
    renderAddToCartModal,
    onAddToCart,
    trackingData,
    index,
    source,
    product,
    rating,
    currentSku,
    comparisonFields,
    shopCollection,
    configOptions: product?.configOptions,
    sectionalExtraLabel,
  }

  //  Coupon logic
  const isCoupon =
    typeof product?.couponDisplayFields === 'object' && Object.keys(product?.couponDisplayFields).length > 0
  if (isCoupon && !isProductTileYMAL) {
    return (
      <PRODUCT_TILE_COUPON
        {...props}
        promotion={product?.promotions?.[`${region}_${zone}`]}
        isChecked={isChecked}
        isCoupon={isCoupon}
        region={region}
        updateVariant={updateVariant}
        isLoading={featureVariant === 'plp' ? false : isLoading}
        isError={isError}
        inViewRef={inViewRef}
        topLevelIsLoading={topLevelIsLoading}
        handleClick={() => {
          navigate(productUrl(product?.route))
          if (typeof handleAnalytics === 'function') handleAnalytics()
        }}
        updateFavorite={handleUpdateFavorites}
        showFinance={isShowFinanceMessageFeatureOn}
        showVariations
      />
    )
  }
  // Return a ProductTile with dynamic data
  return (
    <ProductTileComponent
      {...props}
      isChecked={isChecked}
      isCoupon={isCoupon}
      updateVariant={updateVariant}
      isLoading={featureVariant === 'plp' ? false : isLoading}
      isError={isError}
      inViewRef={inViewRef}
      topLevelIsLoading={topLevelIsLoading}
      handleClick={() => {
        navigate(productUrl(product?.route))
        if (typeof handleAnalytics === 'function') handleAnalytics()
      }}
      updateFavorite={handleUpdateFavorites}
      showFinance={isShowFinanceMessageFeatureOn}
      showVariations={!isCoupon}
    />
  )
}
ProductTile.propTypes = {
  product: object,
  sku: string.isRequired,
  handleAnalytics: PropTypes.func,
  index: PropTypes.number.isRequired,
  source: string,
  currentSku: string,
  comparisonFields: array,
  disableTrackingData: PropTypes.bool,
  sale: PropTypes.bool,
  selectedPill: PropTypes.string,
  topLevelIsLoading: PropTypes.bool,
  orderedProductVariations: object,
  shopCollection: PropTypes.bool,
  featureVariant: string,
  isYMAL: PropTypes.bool,
}

export default ProductTile
